import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  TrendingUp,
  AlertTriangle,
  DollarSign,
  Building2,
  Activity,
  Clock,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { PageHeader } from "@/components/page-header";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { Badge } from "@/components/ui/badge";
import { Link } from "wouter";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";

interface RiskAlertDetail {
  accountId: string;
  accountName: string;
  healthScore: number | null;
  healthRating: string | null;
  lastUpdated: string;
}

interface ExecutiveDashboardData {
  portfolioHealthScore: number | null;
  pipelineValue: number;
  activeDeals: number;
  riskAlerts: number | null;
  riskAlertsDetails: RiskAlertDetail[];
  healthDistribution: {
    excellent: number;
    good: number;
    fair: number;
    poor: number;
    critical: number;
  };
  recentDeals: Array<{
    id: string;
    dealNumber?: string;
    dealName: string;
    transactionStage: string;
    amountRequested: string;
    createdAt: string;
  }>;
  topAccounts: Array<{
    accountId: string;
    accountName: string;
    healthScore: number | null;
    healthRating: string | null;
  }>;
  totalAccounts: number;
}

const healthRatingColors: Record<string, string> = {
  excellent: "#22c55e",
  good: "#3b82f6",
  fair: "#eab308",
  poor: "#f97316",
  critical: "#ef4444",
};

const healthRatingBadgeVariants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  excellent: "default",
  good: "secondary",
  fair: "outline",
  poor: "destructive",
  critical: "destructive",
};

export default function ExecutiveDashboard() {
  const [dateRange, setDateRange] = useState("30d");
  const [healthFilter, setHealthFilter] = useState("all");

  // Parse URL params for initial filter state
  const urlParams = new URLSearchParams(window.location.search);
  const initialHealthFilter = urlParams.get("healthFilter") || "all";

  const effectiveHealthFilter = healthFilter !== "all" ? healthFilter : initialHealthFilter;
  const { data, isLoading } = useQuery<ExecutiveDashboardData>({
    queryKey: ["/api/dashboard/executive", {
      dateRange,
      ...(effectiveHealthFilter && effectiveHealthFilter !== "all" ? { healthRating: effectiveHealthFilter } : {}),
    }],
  });

  // Transform health distribution for chart
  const chartData = data?.healthDistribution
    ? [
        {
          rating: "Excellent",
          key: "excellent",
          count: data.healthDistribution.excellent,
          fill: healthRatingColors.excellent,
        },
        {
          rating: "Good",
          key: "good",
          count: data.healthDistribution.good,
          fill: healthRatingColors.good,
        },
        {
          rating: "Fair",
          key: "fair",
          count: data.healthDistribution.fair,
          fill: healthRatingColors.fair,
        },
        {
          rating: "Poor",
          key: "poor",
          count: data.healthDistribution.poor,
          fill: healthRatingColors.poor,
        },
        {
          rating: "Critical",
          key: "critical",
          count: data.healthDistribution.critical,
          fill: healthRatingColors.critical,
        },
      ]
    : [];

  const formatCurrency = (value: number) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(2)}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}k`;
    } else {
      return `$${value.toLocaleString()}`;
    }
  };

  const hasNoAccounts = !isLoading && data?.totalAccounts === 0;

  return (
    <div className="flex h-screen w-full flex-col" data-testid="page-executive-dashboard">
      <PageHeader
        title="Executive Dashboard"
        breadcrumbs={[
          { label: "Dashboard", href: "/dashboard" },
          { label: "Executive" },
        ]}
      />

      <main className="flex-1 overflow-auto bg-muted/20 p-6">
        <div className="mx-auto max-w-7xl space-y-6">
          {/* Filters Row */}
          <div className="flex flex-wrap gap-4">
            <Select
              value={dateRange}
              onValueChange={setDateRange}
            >
              <SelectTrigger className="w-[180px]" data-testid="filter-date-range">
                <SelectValue placeholder="Select date range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="30d" data-testid="filter-option-30d">30 days</SelectItem>
                <SelectItem value="90d" data-testid="filter-option-90d">90 days</SelectItem>
                <SelectItem value="365d" data-testid="filter-option-365d">365 days</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={healthFilter}
              onValueChange={setHealthFilter}
            >
              <SelectTrigger className="w-[180px]" data-testid="filter-health-rating">
                <SelectValue placeholder="Health rating" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Ratings</SelectItem>
                <SelectItem value="excellent">Excellent</SelectItem>
                <SelectItem value="good">Good</SelectItem>
                <SelectItem value="fair">Fair</SelectItem>
                <SelectItem value="poor">Poor</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Empty State */}
          {hasNoAccounts && (
            <Card data-testid="empty-state-no-accounts">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Building2 className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold">No Accounts Found</h3>
                <p className="text-muted-foreground text-center max-w-md">
                  There are no accounts with financial data matching the current filters.
                  Try adjusting your filters or add financial data to accounts.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="loading-skeleton-metrics">
                {[1, 2, 3].map((i) => (
                  <Card key={i}>
                    <CardHeader className="pb-2">
                      <Skeleton className="h-4 w-24" data-testid={`loading-skeleton-card-${i}`} />
                    </CardHeader>
                    <CardContent>
                      <Skeleton className="h-8 w-16" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Portfolio Metrics Section */}
          {!isLoading && !hasNoAccounts && (
            <>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="section-portfolio-metrics">
                {/* Total Accounts Card */}
                <Card className="hover-elevate" data-testid="card-metric-total-accounts">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">
                      Total Accounts
                    </CardTitle>
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {data?.totalAccounts || 0}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      With health scores
                    </p>
                  </CardContent>
                </Card>

                {/* Average Health Card */}
                <Card className="hover-elevate" data-testid="card-metric-avg-health">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">
                      Average Health Score
                    </CardTitle>
                    <TrendingUp className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {data?.portfolioHealthScore ?? 'Not assessed'}
                      <span className="text-sm font-normal text-muted-foreground">
                        /100
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Portfolio average
                    </p>
                  </CardContent>
                </Card>

                {/* At Risk Card */}
                <Card className="hover-elevate" data-testid="card-metric-at-risk">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">
                      At Risk Accounts
                    </CardTitle>
                    <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div
                      className={`text-2xl font-bold ${
                        (data?.riskAlerts || 0) > 0
                          ? "text-red-600"
                          : "text-green-600"
                      }`}
                    >
                      {data?.riskAlerts ?? 'Not assessed'}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Poor/Critical rating
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Health Distribution Chart */}
              <Card data-testid="chart-health-distribution">
                <CardHeader>
                  <CardTitle>Health Distribution</CardTitle>
                  <CardDescription>
                    Account count by health rating tier
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {data?.portfolioHealthScore == null && <p>Not assessed. Health distribution includes assessed accounts only.</p>}
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="rating" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count">
                        {chartData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.fill}
                            data-testid={`chart-segment-${entry.key}`}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  {/* Chart legend with segment markers for test verification */}
                  <div className="flex flex-wrap justify-center gap-4 mt-4">
                    {chartData.map((entry) => (
                      <div
                        key={entry.key}
                        className="flex items-center gap-2"
                        data-testid={`chart-label-${entry.key}`}
                      >
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: entry.fill }}
                          data-testid={`chart-segment-${entry.key}`}
                        />
                        <span className="text-sm text-muted-foreground">
                          {entry.rating}: {entry.count}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <div className="grid gap-4 md:grid-cols-2">
                {/* Pipeline Overview Section */}
                <Card data-testid="section-pipeline-overview">
                  <CardHeader>
                    <CardTitle>Pipeline Overview</CardTitle>
                    <CardDescription>Active deals and total value</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between" data-testid="metric-pipeline-value">
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium">Total Pipeline Value</span>
                      </div>
                      <span className="text-xl font-bold">
                        {formatCurrency(data?.pipelineValue || 0)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between" data-testid="metric-active-deals">
                      <div className="flex items-center gap-2">
                        <Activity className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium">Active Deals</span>
                      </div>
                      <span className="text-xl font-bold">
                        {data?.activeDeals || 0}
                      </span>
                    </div>
                  </CardContent>
                </Card>

                {/* Risk Alerts Section */}
                <Card data-testid="section-risk-alerts">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-red-500" />
                      Risk Alerts
                    </CardTitle>
                    <CardDescription>
                      Accounts requiring attention
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {data?.riskAlertsDetails && data.riskAlertsDetails.length > 0 ? (
                      <div className="space-y-3">
                        {data.riskAlertsDetails.map((alert, index) => (
                          <Link
                            key={alert.accountId}
                            href={`/accounts/${alert.accountId}`}
                            className="block"
                          >
                            <div
                              data-testid={`alert-item-${index}`}
                              className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 cursor-pointer transition-colors"
                            >
                              <div className="flex-1">
                                <p className="text-sm font-medium hover:underline">
                                  {alert.accountName}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  Score: {alert.healthScore}
                                </p>
                              </div>
                              <Badge
                                variant="destructive"
                                className={alert.healthRating === 'critical' ? 'bg-red-600' : 'bg-orange-500'}
                              >
                                {alert.healthRating}
                              </Badge>
                            </div>
                          </Link>
                        ))}
                      </div>
                    ) : (
                      <div className="text-sm text-muted-foreground text-center py-4">
                        {data?.riskAlerts == null ? 'Not assessed. Open financial data to review the required evidence.' : 'No assessed accounts at risk'}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Top Accounts Table */}
              <Card>
                <CardHeader>
                  <CardTitle>Top Accounts</CardTitle>
                  <CardDescription>
                    Accounts with best health scores
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Table data-testid="table-top-accounts">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Account Name</TableHead>
                        <TableHead className="text-right">Health Score</TableHead>
                        <TableHead className="text-right">Rating</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data?.topAccounts && data.topAccounts.length > 0 ? (
                        data.topAccounts.map((account, index) => (
                          <TableRow
                            key={account.accountId}
                            data-testid={`row-account-${index}`}
                          >
                            <TableCell>
                              <Link
                                href={`/accounts/${account.accountId}`}
                                className="font-medium hover:underline"
                              >
                                {account.accountName}
                              </Link>
                            </TableCell>
                            <TableCell className="text-right">
                              {account.healthScore ?? 'Not assessed'}
                            </TableCell>
                            <TableCell className="text-right">
                              <Badge
                                variant={healthRatingBadgeVariants[account.healthRating ?? ""]}
                                data-testid={`rating-badge-${account.healthRating ?? 'Not assessed'}`}
                              >
                                {account.healthRating ?? 'Not assessed'}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={3} className="text-center text-muted-foreground">
                            No accounts with good+ health scores
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Recent Activity Section */}
              <Card data-testid="section-recent-activity">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    Recent Activity
                  </CardTitle>
                  <CardDescription>Latest deal movements</CardDescription>
                </CardHeader>
                <CardContent>
                  {data?.recentDeals && data.recentDeals.length > 0 ? (
                    <div className="space-y-3">
                      {data.recentDeals.map((deal, index) => (
                        <div
                          key={deal.id}
                          data-testid={`activity-item-${index}`}
                          className="flex items-center justify-between border-b pb-2 last:border-0"
                        >
                          <div>
                            <Link
                              href={`/deals/${deal.id}`}
                              className="text-sm font-medium hover:underline"
                            >
                              {deal.dealNumber || deal.dealName || 'Unnamed Deal'}
                            </Link>
                            <p className="text-xs text-muted-foreground">
                              {deal.dealName}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium">
                              {formatCurrency(
                                parseFloat(deal.amountRequested || "0")
                              )}
                            </p>
                            <Badge variant="outline" className="text-xs">
                              {deal.transactionStage}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div
                      className="text-sm text-muted-foreground text-center py-4"
                      data-testid="empty-state-activity"
                    >
                      No recent activity
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
